import type { PropsWithChildren } from 'react'
import { memo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'

const ProgressContainer = styled.div`
  position: relative;
  flex-shrink: 0;
`

const SVGContainer = styled.svg`
  transform: rotate(-90deg);
  transform-origin: center;
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  width: 100%;
  height: 100%;

  [dir="rtl"] & {
    transform: rotate(-90deg) scaleX(-1);
  }
`

const CircleBackground = styled.circle`
  &.light {
    stroke: ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &.onTierBackground.tier-bronze {
    stroke: ${props => props.theme.palette.product.loyalty.bronze.darkContrast};
  }

  &.onTierBackground.tier-silver {
    stroke: ${props => props.theme.palette.product.loyalty.silver.darkContrast};
  }

  &.onTierBackground.tier-gold {
    stroke: ${props => props.theme.palette.product.loyalty.gold.darkContrast};
  }

  &.onTierBackground.tier-platinum {
    stroke: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }
`

const CircleProgress = styled.circle`
  &.light.tier-bronze {
    stroke: ${props => props.theme.palette.product.loyalty.bronze.darkForeground};
  }

  &.light.tier-silver {
    stroke: ${props => props.theme.palette.product.loyalty.silver.darkForeground};
  }

  &.light.tier-gold {
    stroke: ${props => props.theme.palette.product.loyalty.gold.darkForeground};
  }

  &.light.tier-platinum {
    stroke: ${props => props.theme.palette.product.loyalty.platinum.darkForeground};
  }

  &.onTierBackground.tier-bronze {
    stroke: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
  }

  &.onTierBackground.tier-silver {
    stroke: ${props => props.theme.palette.product.loyalty.silver.darkBackground};
  }

  &.onTierBackground.tier-gold {
    stroke: ${props => props.theme.palette.product.loyalty.gold.darkBackground};
  }

  &.onTierBackground.tier-platinum {
    stroke: ${props => props.theme.palette.product.loyalty.platinum.darkBackground};
  }
`

const ProgressContent = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

interface Props {
  progress: number
  tier: App.LoyaltyTier
  /**
   * 'light' - for white/neutral backgrounds (neutral bg stroke, tier foreground progress)
   * 'onTierBackground' - for tier-colored backgrounds (tier contrast bg stroke, tier background progress)
   * @default 'light'
   */
  variant?: 'light' | 'onTierBackground'
  /**
   * CSS transition duration for the stroke-dashoffset animation (any valid CSS time, e.g. `0.5s`,
   * `1.2s`, `200ms`, `0s`). Passed as `transition` on the progress circle.
   */
  transitionDuration?: string
}

const SIZE = 76

function LoyaltyAccountTileCircularProgressBar(props: PropsWithChildren<Props>) {
  const { progress, children, tier, variant = 'onTierBackground', transitionDuration = '0.5s' } = props
  const circleCoords = Math.round(SIZE / 2)
  const circleRadius = circleCoords - 5
  const circumference = 2 * Math.PI * circleRadius
  const offset = circumference - (progress / 100) * circumference
  const tierClass = `tier-${tier}`

  return (
    <ProgressContainer style={{ width: rem(SIZE), height: rem(SIZE) }}>
      <SVGContainer width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
        <CircleBackground
          className={clsx(variant, tierClass)}
          cx={circleCoords}
          cy={circleCoords}
          r={circleRadius}
          strokeWidth={6}
          fill="none"
        />
        <CircleProgress
          className={clsx(variant, tierClass)}
          cx={circleCoords}
          cy={circleCoords}
          r={circleRadius}
          strokeWidth={7}
          fill="none"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeLinecap="round"
          strokeDashoffset={offset}
          style={{ transition: `stroke-dashoffset ${transitionDuration} ease` }}
        />
      </SVGContainer>
      {children && <ProgressContent>
        {children}
      </ProgressContent>}
    </ProgressContainer>
  )
}

export default memo(LoyaltyAccountTileCircularProgressBar)
